// ============================================================================
// Menu Configuration System
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Updated: 2026-07-05 — Spec 216 IA overhaul (T008): audience-true sections.
//   Insights (renamed Operational Dashboards) · Clients (new) · Internal (new,
//   Admin-only pool for FabricControl-internal tooling) · Network Topology +
//   Governance moved under Operations · single-item sub-headers flattened ·
//   Audit Log → System Logs · removed dead entries (System Health stub,
//   Executive Dashboard, /capacity/list duplicate, Deployments, Settings
//   landing, Agent Management [code iced]). Route-keyed per the R7 label
//   inversion — see specs/216-ui-cleanup/contracts/ia-map.md.
// Description: Centralized menu configuration with role-based visibility
// ============================================================================

import { UserRoleValue } from '@shared/types/user';
import { canAccessArea, navAccessPolicy, type NavAreaId } from '@shared/access/navAccessPolicy';
import { toRole, isSuperAdmin } from '@shared/access/roles';
import type { TenantLicenses } from '@shared/access/licenses';

export interface MenuItemConfig {
  id: string;
  label: string;
  path: string;
  icon?: string;
  roles: UserRoleValue[];
  badge?: {
    type: 'count' | 'status' | 'new';
    value?: number | string;
    color?: 'red' | 'yellow' | 'green' | 'blue' | 'gray';
  };
  children?: MenuItemConfig[];
  order: number;
  section: MenuSection;
  description?: string;
  /** Tooltip text shown when sidebar is collapsed (T005b) */
  tooltip?: string;
  isExternal?: boolean;
  isHidden?: boolean;
  /** Mark as coming soon - shows banner instead of full functionality */
  comingSoon?: boolean;
}

export type MenuSection =
  | 'dashboards'
  | 'insights'
  | 'resources'
  | 'operations'
  | 'clients'
  | 'analytics'
  | 'system'
  | 'internal'
  | 'help';

export interface MenuSectionConfig {
  id: MenuSection;
  label: string;
  order: number;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

// ============================================================================
// Section Definitions (Spec 216 target IA)
// ============================================================================

export const menuSections: MenuSectionConfig[] = [
  { id: 'dashboards', label: 'Dashboards', order: 0, collapsible: false },
  { id: 'insights', label: 'Insights', order: 1, collapsible: true, defaultCollapsed: false },
  { id: 'resources', label: 'Resources', order: 2, collapsible: true, defaultCollapsed: false },
  { id: 'operations', label: 'Operations', order: 3, collapsible: true, defaultCollapsed: false },
  { id: 'clients', label: 'Clients', order: 4, collapsible: true, defaultCollapsed: false },
  { id: 'analytics', label: 'Analytics', order: 5, collapsible: true, defaultCollapsed: false },
  { id: 'system', label: 'System', order: 6, collapsible: true, defaultCollapsed: false },
  // Internal = FabricControl's own plumbing (jobs, sync, SP registry, config).
  // Admin-only in BOTH the menu filter and the route guards (T009).
  { id: 'internal', label: 'Internal', order: 7, collapsible: true, defaultCollapsed: true },
  { id: 'help', label: 'Help & Support', order: 8, collapsible: true, defaultCollapsed: true },
];

// ============================================================================
// Menu Items
// ============================================================================

export const menuItems: MenuItemConfig[] = [
  // ── Dashboards ─────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'home',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 0,
    section: 'dashboards',
    description: 'Overview and quick actions',
  },
  {
    // Owner decision 2026-06-11: alerts are vital operational information.
    id: 'alerts',
    label: 'Alerts',
    path: '/alerts',
    icon: 'bell',
    roles: ['Admin', 'Operator', 'Manager'],
    order: 1,
    section: 'dashboards',
    // (No badge: a count badge with no wired value rendered as an empty red bar.)
    description: 'Active alerts — acknowledge and review fired alerts',
    tooltip: 'Alerts',
  },

  // ── Insights (renamed from Operational Dashboards) ─────────────────────
  // Item Insights deliberately precedes Workspace Insights (owner walkthrough).
  // Warehouse Insights slot filled by Spec 217 (was RESERVED — no empty shell, analyze I2).
  {
    id: 'capacity-insights',
    label: 'Capacity Insights',
    path: '/insights/capacity',
    icon: 'gauge',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 0,
    section: 'insights',
    description: 'Capacity KPIs, usage + sizing trends, right-sizing verdicts',
    tooltip: 'Capacity Insights',
  },
  {
    id: 'item-insights',
    label: 'Fabric Item Insights',
    path: '/operations/fabric-items',
    icon: 'layers',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 1,
    section: 'insights',
    description: 'Inventory of all Fabric items across workspaces',
    tooltip: 'Fabric Item Insights',
  },
  {
    id: 'workspace-insights',
    label: 'Workspace Insights',
    path: '/operations/workspace-inventory',
    icon: 'building',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 2,
    section: 'insights',
    description: 'Workspace item counts and activity status',
    tooltip: 'Workspace Insights',
  },
  {
    id: 'warehouse-insights',
    label: 'Warehouse Insights',
    path: '/insights/warehouse',
    icon: 'database',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 3,
    section: 'insights',
    description: 'Warehouse fleet size, growth, and protection posture',
    tooltip: 'Warehouse Insights',
  },
  {
    id: 'storage-analysis',
    label: 'Storage Analysis',
    path: '/operations/storage-analytics',
    icon: 'hard-drive',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 4,
    section: 'insights',
    description: 'Storage utilization trends and capacity breakdowns',
    tooltip: 'Storage Analysis',
  },
  {
    id: 'azure-infrastructure',
    label: 'Azure Infrastructure',
    path: '/operations/azure-infrastructure',
    icon: 'cloud',
    roles: ['Admin', 'Operator', 'Manager'],
    order: 5,
    section: 'insights',
    description: 'Azure subscriptions, resource groups, and region distribution',
    tooltip: 'Azure Infrastructure',
  },
  {
    // Spec 217 T053 — new-analytics-catalog build (Clarify-approved)
    id: 'cost-insights',
    label: 'Cost Analytics',
    path: '/insights/cost',
    icon: 'dollar-sign',
    roles: ['Admin', 'Manager'],
    order: 6,
    section: 'insights',
    description: 'Capacity + storage + backup-storage spend in one view',
    tooltip: 'Cost Analytics',
  },
  {
    // Spec 217 T057 — new-analytics-catalog build (Clarify-approved)
    id: 'growth-forecast-insights',
    label: 'Growth & Forecast',
    path: '/insights/growth-forecast',
    icon: 'trending-up',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 7,
    section: 'insights',
    description: 'Fleet-wide growth curves with projections',
    tooltip: 'Growth & Forecast Analytics',
  },
  {
    id: 'infrastructure-trends',
    label: 'Infrastructure Trends',
    path: '/infrastructure-trends',
    icon: 'chart-line',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 8,
    section: 'insights',
    description: 'Capacity, storage and pipeline trends, with anomalies',
    tooltip: 'Infrastructure Trends',
  },

  // ── Resources ──────────────────────────────────────────────────────────
  // Single-item sub-headers flattened (Storage Accounts, Workspaces,
  // Lakehouses, Warehouses — the catalogs become tabs of the lists, T029).
  {
    id: 'storage-accounts',
    label: 'Storage Accounts',
    path: '/storage-accounts',
    icon: 'hard-drive',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 0,
    section: 'resources',
    description: 'Azure storage account management',
    tooltip: 'Storage Accounts',
  },
  {
    id: 'capacities',
    label: 'Capacities',
    path: '#',
    icon: 'server',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 1,
    section: 'resources',
    description: 'Fabric capacity management',
    children: [
      {
        // R7: the route is what matters — /capacity-metrics IS the kept
        // Capacity List (the /capacity/list "Capacity Metrics" page was removed).
        id: 'capacity-metrics',
        label: 'Capacity List',
        path: '/capacity-metrics',
        icon: 'list',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 0,
        section: 'resources',
        description: 'All capacities with metrics and detail drill-down',
        tooltip: 'Capacity List',
      },
      {
        id: 'capacity-telemetry',
        label: 'Capacity Telemetry',
        path: '/operations/telemetry',
        icon: 'activity',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 1,
        section: 'resources',
        description: 'Live utilization, pipeline health, insights, and configuration',
        tooltip: 'Capacity Telemetry',
      },
      {
        id: 'capacity-dependencies',
        label: 'Dependencies',
        path: '/capacity/dependencies',
        icon: 'git-branch',
        roles: ['Admin', 'Operator', 'Manager'],
        order: 2,
        section: 'resources',
        description: 'Dependency tree and topology',
        tooltip: 'Dependency Management',
      },
    ],
  },
  {
    id: 'workspaces-list',
    label: 'Workspace List',
    path: '/workspaces',
    icon: 'building',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 2,
    section: 'resources',
    description: 'List all workspaces',
    tooltip: 'Workspace List',
  },
  {
    id: 'lakehouses-all',
    label: 'Lakehouse List',
    path: '/lakehouses',
    icon: 'database',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 3,
    section: 'resources',
    description: 'List all lakehouses (Protection tab carries the catalog)',
    tooltip: 'Lakehouse List',
  },
  {
    id: 'warehouses-all',
    label: 'Warehouse List',
    path: '/warehouses',
    icon: 'hard-drive',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 4,
    section: 'resources',
    description: 'List all warehouses (Protection tab carries the catalog)',
    tooltip: 'Warehouse List',
  },

  // ── Operations ─────────────────────────────────────────────────────────
  // Spec 201 — consolidated Backup command center.
  {
    id: 'backup',
    label: 'Backup Management',
    path: '#', // non-navigable group header; the tab children own the routes
    icon: 'database',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 0,
    section: 'operations',
    description: 'Backup command center — plans, schedules, restore, storage, reports',
    children: [
      {
        id: 'backup-dashboard',
        label: 'Dashboard',
        path: '/backup/dashboard',
        icon: 'layout-dashboard',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 0,
        section: 'operations',
        description: 'Backup command center — coverage, health, restore readiness',
        tooltip: 'Backup Dashboard',
      },
      {
        id: 'backup-plans',
        label: 'Plans',
        path: '/backup/plans',
        icon: 'list-checks',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 1,
        section: 'operations',
        description: 'Backup plans — schedule, retention, destination',
        tooltip: 'Backup Plans',
      },
      {
        id: 'backup-management',
        label: 'Management',
        path: '/backup/management',
        icon: 'network',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 2,
        section: 'operations',
        description: 'Entity → plan assignments — protect and move entities between plans',
        tooltip: 'Backup Assignments',
      },
      {
        id: 'backup-schedules',
        label: 'Schedules',
        path: '/backup/schedules',
        icon: 'clock',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 3,
        section: 'operations',
        description: 'Graphical backup schedule builder',
        tooltip: 'Backup Schedules',
      },
      {
        id: 'backup-restore',
        label: 'Restore',
        path: '/backup/restore',
        icon: 'history',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 4,
        section: 'operations',
        description: 'Restore-point browser and launch',
        tooltip: 'Restore',
      },
      {
        id: 'backup-storage',
        label: 'Backups',
        path: '/backup/storage',
        icon: 'hard-drive',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 5,
        section: 'operations',
        description: 'Storage assignment + SP grant',
        tooltip: 'Backup Storage',
      },
      {
        id: 'backup-health',
        label: 'Health',
        path: '/backup/health',
        icon: 'heart-pulse',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 6,
        section: 'operations',
        description: 'Backup health — diagnose problematic backups; exclude / repoint / retry (Spec 211)',
        tooltip: 'Backup Health',
      },
        {
          id: 'backup-errors',
          label: 'Errors',
          path: '/backup/errors',
          icon: 'alert-circle',
          roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
          order: 75,
          section: 'operations',
          description: 'Backup errors with attributed cause and guided remediation',
          tooltip: 'What actually failed, why, and what to do about it',
        },
      {
        id: 'backup-recoverability',
        label: 'Recoverability',
        path: '/backup/recoverability',
        icon: 'shield-check',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 7,
        section: 'operations',
        description: 'Proof of recoverability — automated verification + score, secondary copies (3-2-1-1-0)',
        tooltip: 'Recoverability',
      },
      {
        id: 'backup-reports',
        label: 'Reports',
        path: '/backup/reports',
        icon: 'file-text',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 8,
        section: 'operations',
        description: 'Compliance + completion reports (on-demand + scheduled email)',
        tooltip: 'Backup Reports',
      },
      {
        id: 'backup-settings',
        label: 'Settings',
        path: '/backup/settings',
        icon: 'sliders-horizontal',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 9,
        section: 'operations',
        description: 'Protection tiers (RPO/RTO) + retention policies — the central SLA definitions',
        tooltip: 'Backup Settings',
      },
      {
        id: 'compare',
        label: 'Delta Compare',
        path: '/compare',
        icon: 'git-compare',
        roles: ['Admin', 'Operator', 'Manager'],
        order: 10,
        section: 'operations',
        description: 'Compare delta tables between backups',
        tooltip: 'Delta Compare',
      },
      {
        // Spec 256 — Backup Cost Governance
        id: 'backup-cost',
        label: 'Cost & Controls',
        path: '/backup/cost',
        icon: 'sliders-horizontal',
        roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
        order: 11,
        section: 'operations',
        description: 'WORM / recoverability switches, egress offenders, and the cost-optimization advisor',
        tooltip: 'Cost & Controls',
      },
    ],
  },
  // Workflows (Feature 135) — moved from Dashboards; it is an operational
  // onboarding worklist, not a role dashboard.
  {
    id: 'workflows',
    label: 'Workflows',
    path: '/workflows',
    icon: 'workflow',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 1,
    section: 'operations',
    description: 'Onboarding progress, bottlenecks, and next actions',
    tooltip: 'Workflows',
  },
  {
    id: 'platform-health',
    label: 'Platform Health',
    path: '/operations/platform-health',
    icon: 'heart-pulse',
    roles: ['Admin', 'Operator', 'Manager'],
    order: 2,
    section: 'operations',
    description: 'FabricControlPlane self-monitoring — is our own platform running?',
    tooltip: 'Platform Health',
  },
  {
    id: 'infrastructure-health',
    label: 'Infrastructure Health',
    path: '/infrastructure/health',
    icon: 'heart-pulse',
    roles: ['Admin', 'Operator'],
    order: 3,
    section: 'operations',
    description: 'Collected reachability + status of the Azure estate we manage',
    tooltip: 'Infrastructure Health',
  },
  // Feature #140: Network Topology — moved from Infrastructure section.
  {
    id: 'network-topology',
    label: 'Network Topology',
    path: '#', // non-navigable group header; the 'Network Overview' child owns the route
    icon: 'network',
    roles: ['Admin', 'Operator', 'Manager'],
    order: 4,
    section: 'operations',
    description: 'Network topology and security overview',
    children: [
      {
        // Spec 217 T045: relabeled "Network Overview" → "Network Insights" (naming family);
        // same route/roles/section — CIDR-overlap rollout-readiness panel added to the page.
        id: 'network-overview',
        label: 'Network Insights',
        path: '/operations/network-topology',
        icon: 'globe',
        roles: ['Admin', 'Operator', 'Manager'],
        order: 0,
        section: 'operations',
        description: 'VNets, subnets, peering health, and CIDR rollout readiness',
      },
      {
        id: 'private-endpoints',
        label: 'Private Endpoints',
        path: '/operations/network-topology/private-endpoints',
        icon: 'lock',
        roles: ['Admin', 'Operator', 'Manager'],
        order: 1,
        section: 'operations',
        description: 'Private endpoint inventory and status',
      },
      {
        id: 'nsg-summary',
        label: 'NSG Summary',
        path: '/operations/network-topology/nsg-rules',
        icon: 'shield',
        roles: ['Admin', 'Operator', 'Manager'],
        order: 2,
        section: 'operations',
        description: 'Network security group audit',
      },
    ],
  },
  // Feature #142: Governance — moved from Infrastructure; ONE entry, the
  // sub-pages render as in-page tabs (Spec 216 T031).
  {
    id: 'governance',
    label: 'Governance',
    path: '/operations/governance',
    icon: 'shield-check',
    roles: ['Admin', 'Operator'],
    order: 5,
    section: 'operations',
    description: 'Governance compliance — tenant settings, access, shared artifacts, lineage, gateways',
    tooltip: 'Governance',
  },
  {
    id: 'pipeline-monitoring',
    label: 'Pipeline Monitoring',
    path: '/operations/pipeline-monitoring',
    icon: 'activity',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 6,
    section: 'operations',
    description: 'Universal-logging-driven pipeline monitoring — Dashboard + Job Activities + predictability (Spec 206)',
    tooltip: 'Pipeline Monitoring',
  },
  {
    id: 'ods-monitoring',
    label: 'Data Pipeline Monitoring',
    path: '/operations/ods-monitoring',
    icon: 'database',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 7,
    section: 'operations',
    description: 'Legacy ODS and DW / Claims pipeline job monitoring (historical logs)',
    tooltip: 'Data Pipeline Monitoring',
  },

  // ── Clients (new section — the client pool) ────────────────────────────
  {
    id: 'clients',
    label: 'Client List',
    path: '/admin/clients',
    icon: 'building-2',
    roles: ['Admin', 'Operator', 'Manager'],
    order: 0,
    section: 'clients',
    description: 'Client account management',
    tooltip: 'Client List',
  },
  {
    id: 'sla-blast-radius',
    label: 'SLA Blast Radius',
    path: '/operations/sla-blast-radius',
    icon: 'git-branch',
    roles: ['Admin', 'Operator', 'Manager'],
    order: 1,
    section: 'clients',
    description: 'Capacity dependencies and SLA impact analysis',
    tooltip: 'SLA Blast Radius',
  },
  {
    id: 'settings-sla',
    label: 'SLA Configuration',
    path: '/settings/sla',
    icon: 'layers',
    roles: ['Admin', 'Operator'],
    order: 2,
    section: 'clients',
    description: 'SLA tiers, client assignments, and maintenance windows',
    tooltip: 'SLA Configuration',
  },

  // ── Analytics ──────────────────────────────────────────────────────────
  {
    id: 'metadata',
    label: 'Metadata',
    path: '/metadata',
    icon: 'layers',
    roles: ['Admin', 'Operator'],
    order: 0,
    section: 'analytics',
    description: 'Semantic model definitions, lineage, refresh history, and item schedules',
    tooltip: 'Metadata Explorer',
  },

  // ── System ─────────────────────────────────────────────────────────────
  {
    id: 'users',
    label: 'Users',
    path: '/admin/users',
    icon: 'users',
    roles: ['Admin'],
    order: 0,
    section: 'system',
    description: 'User management',
  },
  {
    id: 'system-logs',
    label: 'System Logs',
    path: '#',
    icon: 'scroll-text',
    roles: ['Admin'],
    order: 1,
    section: 'system',
    description: 'System and authentication logs',
    children: [
      {
        id: 'audit-system',
        label: 'System Audit Log',
        path: '/audit/system',
        icon: 'file-text',
        roles: ['Admin'],
        order: 0,
        section: 'system',
        description: 'Entity change audit trail',
        tooltip: 'System Audit Log',
      },
      {
        id: 'audit-auth',
        label: 'Authentication Log',
        path: '/audit/auth',
        icon: 'shield',
        roles: ['Admin'],
        order: 1,
        section: 'system',
        description: 'Authentication event log',
        tooltip: 'Authentication Log',
      },
    ],
  },
  // Alert Rules keeps a direct entry (analyze I4: user-facing config, future
  // Spec-193 surface). The Settings landing page + group were removed.
  {
    id: 'settings-alert-rules',
    label: 'Alert Rules',
    path: '/settings/alert-rules',
    icon: 'bell',
    roles: ['Admin', 'Operator'],
    order: 2,
    section: 'system',
    description: 'Alert rule configuration',
    tooltip: 'Alert Rules',
  },
  // Spec 285 — SuperAdmin-only (narrowed by the shared navAccessPolicy area
  // 'system.tenant-licenses'; the static roles list mirrors its System neighbours).
  {
    id: 'tenant-licenses',
    label: 'Tenant Licenses',
    path: '/admin/tenant-licenses',
    icon: 'lock',
    roles: ['Admin'],
    order: 3,
    section: 'system',
    description: 'View a tenant\'s licence as Fabric Control Management issued it',
    tooltip: 'Tenant Licenses',
  },

  // ── Internal (Admin-only — FabricControl's own plumbing) ───────────────
  {
    id: 'jobs',
    label: 'Collection Jobs',
    path: '/jobs',
    icon: 'play-circle',
    roles: ['Admin'],
    order: 0,
    section: 'internal',
    description: 'Collection job runs, scheduling, logs, and benchmarks',
    tooltip: 'Collection Jobs',
  },
  {
    id: 'discovery',
    label: 'Infrastructure Sync',
    path: '/discovery',
    icon: 'network',
    roles: ['Admin'],
    order: 1,
    section: 'internal',
    description: 'Infrastructure sync run history',
    tooltip: 'Infrastructure Sync',
  },
  {
    id: 'deployment-history',
    label: 'Deployment History',
    path: '/operations/deployment-history',
    icon: 'clock',
    roles: ['Admin'],
    order: 2,
    section: 'internal',
    description: 'Infrastructure deployment audit trail',
    tooltip: 'Deployment History',
  },
  {
    id: 'maintenance',
    label: 'Maintenance',
    path: '/maintenance',
    icon: 'wrench',
    roles: ['Admin'],
    order: 3,
    section: 'internal',
    description: 'Lakehouse maintenance operations',
  },
  {
    id: 'reconciliation',
    label: 'Reconciliation',
    path: '/reconciliation',
    icon: 'refresh-cw',
    roles: ['Admin'],
    order: 4,
    section: 'internal',
    description: 'Collection-vs-reality reconciliation runs',
  },
  {
    id: 'infrastructure-config',
    label: 'Configuration',
    path: '/infrastructure',
    icon: 'settings',
    roles: ['Admin'],
    order: 5,
    section: 'internal',
    description: 'Infrastructure configuration',
  },
  {
    id: 'service-principals',
    label: 'Service Principals',
    path: '/service-principals',
    icon: 'bookmark',
    roles: ['Admin'],
    order: 6,
    section: 'internal',
    description: 'Service principal registry',
  },
  {
    id: 'service-principals-reconciliation',
    label: 'SP Reconciliation',
    path: '/service-principals/reconciliation',
    icon: 'refresh-cw',
    roles: ['Admin'],
    order: 7,
    section: 'internal',
    description: 'Service principal reconciliation status',
  },

  // ── Help & Support ─────────────────────────────────────────────────────
  {
    id: 'documentation',
    label: 'Documentation',
    path: '/help/docs',
    icon: 'book-open',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 0,
    section: 'help',
    description: 'Platform documentation and guides',
    tooltip: 'Documentation',
  },
  {
    id: 'runbooks',
    label: 'Runbooks',
    path: '/help/runbooks',
    icon: 'file-code',
    roles: ['Admin', 'Operator', 'Manager'],
    order: 1,
    section: 'help',
    description: 'Operational runbooks and procedures',
    tooltip: 'Runbooks',
  },
  {
    id: 'support',
    label: 'Support',
    path: '/help/support',
    icon: 'life-buoy',
    roles: ['Admin', 'Operator', 'Manager', 'Viewer'],
    order: 2,
    section: 'help',
    description: 'Submit support requests',
    tooltip: 'Support',
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

// Alias for backward compatibility
type UserRole = UserRoleValue;

/**
 * Spec 285 — menu item id → the gated navigation area that governs it, derived from the ONE
 * shared navAccessPolicy (its `menuItemIds`). The menu never hard-codes its own gates: an
 * item is gated exactly as the policy says, which is the same decision requireNavAccess
 * enforces on the routes behind it — so visibility equals reachability (FR-001/FR-002).
 */
const areaByMenuItemId: ReadonlyMap<string, NavAreaId> = new Map(
  navAccessPolicy.flatMap((rule) => rule.menuItemIds.map((id) => [id, rule.area] as const)),
);

/**
 * Is one menu item visible to this principal? (children are judged separately by the caller)
 * - hidden items never show;
 * - SuperAdmin passes the item's role list (it is the full-access role, FR-004);
 * - everyone else must be in the item's own `roles`;
 * - if the policy gates the item, the shared canAccessArea (role set ∧ license) must allow it.
 */
function isVisibleTo(item: MenuItemConfig, role: UserRole, licenses: TenantLicenses): boolean {
  if (item.isHidden) return false;
  const parsed = toRole(role);
  const superAdmin = parsed !== null && isSuperAdmin(parsed);
  if (!superAdmin && !item.roles.includes(role)) return false;
  const area = areaByMenuItemId.get(item.id);
  return !area || canAccessArea(area, { role, licenses });
}

/**
 * Get menu items visible to a principal (role + the viewing tenant's licenses).
 * `licenses` is REQUIRED — a caller that omitted it would silently hide licensed features.
 */
export function getMenuItemsForRole(role: UserRole, licenses: TenantLicenses): MenuItemConfig[] {
  const filterByRole = (items: MenuItemConfig[]): MenuItemConfig[] => {
    return items
      .filter((item) => isVisibleTo(item, role, licenses))
      .map((item) => ({
        ...item,
        children: item.children ? filterByRole(item.children) : undefined,
      }))
      .filter((item) => !item.children || item.children.length > 0);
  };

  return filterByRole(menuItems);
}

/**
 * Get menu items grouped by section
 */
export function getMenuItemsBySection(role: UserRole, licenses: TenantLicenses): Map<MenuSection, MenuItemConfig[]> {
  const items = getMenuItemsForRole(role, licenses);
  const grouped = new Map<MenuSection, MenuItemConfig[]>();

  menuSections.forEach((section) => {
    const sectionItems = items
      .filter((item) => item.section === section.id)
      .sort((a, b) => a.order - b.order);

    if (sectionItems.length > 0) {
      grouped.set(section.id, sectionItems);
    }
  });

  return grouped;
}

/**
 * Get flattened list of all menu paths for routing
 */
export function getAllMenuPaths(): string[] {
  const paths: string[] = [];

  const extractPaths = (items: MenuItemConfig[]) => {
    items.forEach((item) => {
      paths.push(item.path);
      if (item.children) {
        extractPaths(item.children);
      }
    });
  };

  extractPaths(menuItems);
  return paths;
}

/**
 * Real, navigable pages a user can add to Favorites — flattened from the role-filtered
 * live menu (so ONLY pages that actually exist and the user can reach appear). Excludes
 * external links and coming-soon placeholders; de-duped by path. This is the single source
 * for the Favorites picker (replaces the old hard-coded, now-stale searchMenuItems list).
 */
export interface FavoritablePage {
  title: string;
  path: string;
  section: MenuSection;
  sectionLabel: string;
}

export function getFavoritablePages(role: UserRole, licenses: TenantLicenses): FavoritablePage[] {
  const sectionLabel = new Map(menuSections.map((s) => [s.id, s.label] as const));
  const out: FavoritablePage[] = [];
  const seen = new Set<string>();
  const walk = (items: MenuItemConfig[]): void => {
    for (const item of items) {
      if (
        item.path &&
        item.path.startsWith('/') &&
        !item.isExternal &&
        !item.comingSoon &&
        !seen.has(item.path)
      ) {
        seen.add(item.path);
        out.push({
          title: item.label,
          path: item.path,
          section: item.section,
          sectionLabel: sectionLabel.get(item.section) ?? String(item.section),
        });
      }
      if (item.children) walk(item.children);
    }
  };
  walk(getMenuItemsForRole(role, licenses));
  return out.sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Find menu item by path
 */
export function findMenuItemByPath(path: string): MenuItemConfig | undefined {
  const search = (items: MenuItemConfig[]): MenuItemConfig | undefined => {
    for (const item of items) {
      if (item.path === path) return item;
      if (item.children) {
        const found = search(item.children);
        if (found) return found;
      }
    }
    return undefined;
  };

  return search(menuItems);
}

/**
 * Get breadcrumb trail for a path
 */
export function getBreadcrumbTrail(path: string): MenuItemConfig[] {
  const trail: MenuItemConfig[] = [];

  const search = (items: MenuItemConfig[], parents: MenuItemConfig[]): boolean => {
    for (const item of items) {
      if (item.path === path) {
        trail.push(...parents, item);
        return true;
      }
      if (item.children) {
        if (search(item.children, [...parents, item])) {
          return true;
        }
      }
    }
    return false;
  };

  search(menuItems, []);
  return trail;
}

export default { menuItems, menuSections, getMenuItemsForRole, getMenuItemsBySection };
